import type { RootState } from "../../app/Store";
import { AssetKey } from "../../dto/AssetDTO";
import type { PropMetadata } from "../../dto/PropMetadata";
import { InteractionPropertyType } from "../../models/InteractionProperty";
import { PropType, type Prop } from "../../models/Prop";
import type { Variable } from "../../models/interactions/Variable";
import {
  isBuiltInVariableId,
  isReservedSequenceVariableId,
} from "../../models/interactions/BuiltInSequenceVariables";
import type Take3D from "../../models/Take3D";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { DynamicInteractionPropertyType } from "../../models/interactions/DynamicInteraction";
import { AssetService } from "../../services/AssetService";
import { OutfitService } from "../../services/OutfitService";
import type { Outfit } from "../../models/Outfit";
import type {
  ClipboardAssetDependency,
  ClipboardContainerLocator,
  ClipboardDependency,
  ClipboardDependencyKind,
  ClipboardEnvelope,
  ClipboardPropDependency,
  ClipboardPropMetadataDependency,
  ClipboardSourceContext,
} from "./copyPasteClipboard";
import { isClipboardDependency } from "./copyPasteClipboard";
import { isNoLookat } from "../../models/NoLookat";

export interface ClipboardReferenceAdapter {
  portability: "portable" | "dependencyAware";
}

const portable = { portability: "portable" } as const;
const dependencyAware = { portability: "dependencyAware" } as const;

/**
 * This literal is deliberately exhaustive. Adding an InteractionType without
 * choosing a clipboard policy is a TypeScript error.
 */
export const clipboardReferenceAdapters = {
  [InteractionType.Default]: portable,
  [InteractionType.TeleportMarker]: portable,
  [InteractionType.Marker]: portable,
  [InteractionType.LisaMessage]: portable,
  [InteractionType.MessagePopup]: portable,
  [InteractionType.ImagePopup]: dependencyAware,
  [InteractionType.VideoPopup]: dependencyAware,
  [InteractionType.PdfPopup]: dependencyAware,
  [InteractionType.MultipleChoiceQuiz]: dependencyAware,
  [InteractionType.MultiselectQuiz]: dependencyAware,
  [InteractionType.MultiSelectGraphicsQuiz]: dependencyAware,
  [InteractionType.MultipleChoiceGraphicsQuiz]: dependencyAware,
  [InteractionType.OrderQuiz]: dependencyAware,
  [InteractionType.OrderGraphicsQuiz]: dependencyAware,
  [InteractionType.ConnectQuiz]: dependencyAware,
  [InteractionType.ConnectGraphicsQuiz]: dependencyAware,
  [InteractionType.LabelDragAndDropQuiz]: dependencyAware,
  [InteractionType.PropLabelQuiz]: dependencyAware,
  [InteractionType.PropLabelOrderQuiz]: dependencyAware,
  [InteractionType.ImageDragAndDropQuiz]: dependencyAware,
  [InteractionType.ScrabbleQuiz]: dependencyAware,
  [InteractionType.WordSpinnerQuiz]: dependencyAware,
  [InteractionType.ClickRegion]: portable,
  [InteractionType.ClickRegionOrderQuiz]: dependencyAware,
  [InteractionType.ClickRegionMultiSelectQuiz]: dependencyAware,
  [InteractionType.WordQuiz]: dependencyAware,
  [InteractionType.PhotoRangeQuiz]: dependencyAware,
  [InteractionType.AmbientAudio]: dependencyAware,
  [InteractionType.AudioPopup]: dependencyAware,
  [InteractionType.Conversation]: dependencyAware,
  [InteractionType.SetCamera]: dependencyAware,
  [InteractionType.MessageBubble]: dependencyAware,
  [InteractionType.EmotionBubble]: dependencyAware,
  [InteractionType.DialogQuiz]: dependencyAware,
  [InteractionType.Grab]: dependencyAware,
  [InteractionType.DynamicInteraction]: dependencyAware,
  [InteractionType.Detach]: dependencyAware,
  [InteractionType.Attach]: dependencyAware,
  [InteractionType.Transition]: dependencyAware,
  [InteractionType.Slideshow]: dependencyAware,
  [InteractionType.ButtonsQuiz]: dependencyAware,
  [InteractionType.Lookat]: dependencyAware,
  [InteractionType.SetPropState]: dependencyAware,
  [InteractionType.ShowUIProp]: dependencyAware,
  [InteractionType.HideUIProp]: dependencyAware,
  [InteractionType.ImageButtonQuiz]: dependencyAware,
  [InteractionType.ClickIndicator]: dependencyAware,
  [InteractionType.HideClickIndicator]: dependencyAware,
  [InteractionType.StatementQuiz]: portable,
  [InteractionType.InspectProp]: dependencyAware,
  [InteractionType.Wait]: portable,
  [InteractionType.SetCharacterParts]: dependencyAware,
  [InteractionType.ClearCharacterParts]: dependencyAware,
  [InteractionType.SetOutfit]: dependencyAware,
  [InteractionType.Sequence]: dependencyAware,
  [InteractionType.ParallelSequence]: portable,
  [InteractionType.AnimateActor]: dependencyAware,
  [InteractionType.StopWalking]: dependencyAware,
  [InteractionType.ProximityTrigger]: dependencyAware,
  [InteractionType.SendActorToPosition]: dependencyAware,
  [InteractionType.SubSequence]: portable,
  [InteractionType.Trigger]: portable,
  [InteractionType.PlaySubSequence]: dependencyAware,
  [InteractionType.StopSubSequence]: dependencyAware,
  [InteractionType.TriggerEvent]: portable,
  [InteractionType.WaitForEvent]: portable,
  [InteractionType.WaitForClick]: dependencyAware,
  [InteractionType.InitializeTimer]: portable,
  [InteractionType.UpdateTimer]: portable,
  [InteractionType.GetTimerTime]: dependencyAware,
  [InteractionType.ShowCustomQuiz]: portable,
  [InteractionType.LoseCustomQuizStar]: portable,
  [InteractionType.CloseCustomQuiz]: portable,
  [InteractionType.ShowLetterBox]: portable,
  [InteractionType.HideLetterBox]: portable,
  [InteractionType.If]: dependencyAware,
  [InteractionType.Switch]: dependencyAware,
  [InteractionType.Math]: dependencyAware,
  [InteractionType.SetVariable]: dependencyAware,
  [InteractionType.SetMovementEnabled]: portable,
  [InteractionType.EnableDefaultCamera]: portable,
  // Slides hold no styling, so a deck adopts the destination theme on paste.
  // Asset slots (Phase 3) are the reason this is not portable.
  [InteractionType.PowerSlide]: dependencyAware,
} satisfies Record<InteractionType, ClipboardReferenceAdapter>;

type RecordValue = Record<string, unknown>;

interface WalkLocation {
  ownerId: string;
  ownerType?: InteractionType;
  inSequence: boolean;
  slot: string;
  propertyName: string;
  parent: RecordValue | unknown[];
  key: string | number;
  parentObject?: RecordValue;
  parentPath: string;
}

interface ReferenceSourceLookup {
  actorsById: Map<string, { workingName?: string; preset?: string }>;
  propsById: Map<string, Prop>;
  variablesById: Map<string, Variable>;
  environmentId: string;
  environmentNames: Map<string, string>;
}

const interactionTypes = new Set<string>(Object.values(InteractionType));
const actorFieldNames = new Set([
  "actorId",
  "npcId",
  "sourceNpcId",
  "targetId",
  "playerGuid",
  "lookAt",
  "lookat",
  "targetId",
]);
const lookatFieldNames = new Set(["targetId", "lookAt", "lookat"]);
const propFieldNames = new Set(["propGuid", "propId", "instanceId"]);
const assetFieldNames = new Set([
  "assetId",
  "assetIds",
  "imageAssetId",
  "audioAssetId",
  "baseImage",
  "titleIconAssetId",
  "bottomIconAssetId",
  "assetGuid",
]);
const directVariableFieldNames = new Set([
  "variable",
  "output",
  "storeDataKey",
]);
const maybeVariableFieldNames = new Set(["match", "input", "amount", "data"]);
const environmentPositionFieldNames = new Set(["positionId", "posId"]);
const requiredFieldNames = new Set([
  "actorId",
  "npcId",
  "sourceNpcId",
  "playerGuid",
  "propGuid",
  "propId",
  "instanceId",
  "source",
  "cameraId",
  "positionId",
  "posId",
  "variable",
  "output",
  "nodeToPlay",
]);

function isRecord(value: unknown): value is RecordValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isInteraction(value: unknown): value is Interaction {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.type === "string" &&
    interactionTypes.has(value.type)
  );
}

function appendSlot(base: string, key: string | number): string {
  return typeof key === "number"
    ? `${base}[${key}]`
    : base
      ? `${base}.${key}`
      : key;
}

function cloneClipboardValue<T>(value: T): T {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

function walkReferenceValues(
  value: unknown,
  callback: (value: string, location: WalkLocation) => void,
  initialInSequence = false,
): void {
  const visited = new Set<object>();

  const visit = (
    current: unknown,
    ownerId: string,
    ownerType: InteractionType | undefined,
    path: string,
    inheritedPropertyName = "",
    inSequence = false,
  ) => {
    if (current === null || typeof current !== "object") return;
    if (visited.has(current as object)) return;
    visited.add(current as object);

    let nextOwnerId = ownerId;
    let nextOwnerType = ownerType;
    let nextPath = path;
    let nextInSequence = inSequence;
    if (isInteraction(current)) {
      nextOwnerId = current.id;
      nextOwnerType = current.type;
      nextPath = "";
      nextInSequence =
        nextInSequence || current.type === InteractionType.Sequence;
    } else if (
      !nextOwnerId &&
      isRecord(current) &&
      typeof current.id === "string"
    ) {
      nextOwnerId = current.id;
    }

    const entries: Array<[string | number, unknown]> = Array.isArray(current)
      ? current.map((item, index) => [index, item])
      : Object.entries(current);

    for (const [key, child] of entries) {
      const slot = appendSlot(nextPath, key);
      const propertyName =
        typeof key === "number" ? inheritedPropertyName : String(key);
      // "No one" in a look-at slot points at nothing, so it is not a reference
      const isNoLookatSlot =
        isNoLookat(child as string) && lookatFieldNames.has(propertyName);
      if (typeof child === "string" && child !== "" && !isNoLookatSlot) {
        callback(child, {
          ownerId: nextOwnerId || "clipboard-root",
          ownerType: nextOwnerType,
          inSequence: nextInSequence,
          slot,
          propertyName,
          parent: current as RecordValue | unknown[],
          key,
          parentObject: Array.isArray(current)
            ? undefined
            : (current as RecordValue),
          parentPath: nextPath,
        });
      } else if (child !== null && typeof child === "object") {
        visit(
          child,
          nextOwnerId,
          nextOwnerType,
          slot,
          propertyName,
          nextInSequence,
        );
      }
    }
  };

  visit(value, "", undefined, "", "", initialInSequence);
}

function getSourceLookup(state: RootState): ReferenceSourceLookup {
  const actors = state.training?.actors ?? [];
  const props = state.training?.props ?? [];
  const variables = state.training?.sequenceVariables ?? [];
  const interactionPoints = state.environment?.interactionPoints ?? [];
  const environmentNames = new Map<string, string>();
  interactionPoints.forEach((takeLocation) => {
    environmentNames.set(takeLocation.id, takeLocation.name);
    takeLocation.actor_positions?.forEach((position) =>
      environmentNames.set(position.id, position.name),
    );
    takeLocation.prop_positions?.forEach((position) =>
      environmentNames.set(position.id, position.name),
    );
    takeLocation.camera_positions?.forEach((position) =>
      environmentNames.set(position.id, position.name),
    );
  });
  return {
    actorsById: new Map(
      actors.map((actor) => [
        actor.id,
        { workingName: actor.workingName, preset: actor.preset },
      ]),
    ),
    propsById: new Map(props.map((prop) => [prop.id, prop])),
    variablesById: new Map(
      variables.map((variable) => [variable.id, variable]),
    ),
    environmentId: state.environment?.currentEnvironmentId ?? "",
    environmentNames,
  };
}

function expectedAssetType(
  propertyName: string,
  ownerType?: InteractionType,
): ClipboardAssetDependency["expectedType"] {
  if (
    propertyName.toLowerCase().includes("image") ||
    propertyName === "baseImage" ||
    propertyName.toLowerCase().includes("icon")
  ) {
    return "image";
  }
  if (propertyName.toLowerCase().includes("audio")) return "audio";
  if (ownerType === InteractionType.VideoPopup) return "video";
  if (ownerType === InteractionType.PdfPopup) return "pdf";
  if (ownerType === InteractionType.AmbientAudio) return "audio";
  if (
    ownerType === InteractionType.ImagePopup ||
    ownerType === InteractionType.ImageDragAndDropQuiz ||
    ownerType === InteractionType.LabelDragAndDropQuiz ||
    ownerType === InteractionType.MultiSelectGraphicsQuiz ||
    ownerType === InteractionType.MultipleChoiceGraphicsQuiz ||
    ownerType === InteractionType.OrderGraphicsQuiz ||
    ownerType === InteractionType.ConnectGraphicsQuiz ||
    ownerType === InteractionType.PhotoRangeQuiz
  ) {
    return "image";
  }
  return "unknown";
}

function findParentPropSlot(location: WalkLocation): string | undefined {
  const parent = location.parentObject;
  if (!parent) return undefined;
  for (const key of ["propGuid", "propId", "source"]) {
    if (typeof parent[key] === "string" && parent[key] !== "") {
      return appendSlot(location.parentPath, key);
    }
  }
  return undefined;
}

function inferDependency(
  sourceId: string,
  location: WalkLocation,
  lookup?: ReferenceSourceLookup,
): ClipboardDependency | undefined {
  const base = {
    interactionId: location.ownerId,
    slot: location.slot,
    sourceId,
    required: requiredFieldNames.has(location.propertyName),
  };
  const parent = location.parentObject;

  if (
    parent?.type === InteractionPropertyType.actor &&
    location.propertyName === "value"
  ) {
    const actor = lookup?.actorsById.get(sourceId);
    return { kind: "actor", ...base, ...actor };
  }
  if (
    parent?.type === DynamicInteractionPropertyType.actorDropdown &&
    location.propertyName === "value"
  ) {
    const actor = lookup?.actorsById.get(sourceId);
    return { kind: "actor", ...base, ...actor };
  }

  if (location.propertyName === "nodeToPlay") {
    return { kind: "sequenceNode", ...base };
  }
  if (assetFieldNames.has(location.propertyName)) {
    return {
      kind: "asset",
      ...base,
      expectedType: expectedAssetType(
        location.propertyName,
        location.ownerType,
      ),
    };
  }
  if (location.propertyName === "cameraId") {
    return {
      kind: "environmentCamera",
      ...base,
      environmentId: lookup?.environmentId,
      displayName: lookup?.environmentNames.get(sourceId),
    };
  }
  if (
    location.propertyName === "takeLocationId" ||
    location.propertyName === "interactionPointId"
  ) {
    return {
      kind: "takeLocation",
      ...base,
      environmentId: lookup?.environmentId,
      displayName: lookup?.environmentNames.get(sourceId),
    };
  }
  if (environmentPositionFieldNames.has(location.propertyName)) {
    return {
      kind: "environmentPosition",
      ...base,
      environmentId: lookup?.environmentId,
      displayName: lookup?.environmentNames.get(sourceId),
    };
  }
  const isSequenceLookatTarget =
    location.inSequence &&
    ((location.ownerType === InteractionType.Lookat &&
      location.propertyName === "targetId") ||
      ((location.ownerType === InteractionType.MessageBubble ||
        location.ownerType === InteractionType.EmotionBubble) &&
        location.propertyName === "lookAt"));
  if (isSequenceLookatTarget) {
    // Sequence nodes target environment positions. Standalone interactions
    // use these same fields for Actor or Prop references.
    return {
      kind: "environmentPosition",
      ...base,
      environmentId: lookup?.environmentId,
      displayName: lookup?.environmentNames.get(sourceId),
    };
  }
  if (
    directVariableFieldNames.has(location.propertyName) ||
    (maybeVariableFieldNames.has(location.propertyName) &&
      (lookup?.variablesById.has(sourceId) || sourceId.startsWith("GV_")))
  ) {
    const variable = lookup?.variablesById.get(sourceId);
    return {
      kind: "variable",
      ...base,
      name: variable?.name,
      variableType: variable?.type,
      scope: variable?.scope,
      builtIn: isBuiltInVariableId(sourceId),
    };
  }
  if (
    location.propertyName === "outfitName" ||
    (location.propertyName === "outfit" && parent?.actorId !== undefined)
  ) {
    return { kind: "outfit", ...base };
  }
  if (
    ["partId", "malePartId", "femalePartId", "variantId"].includes(
      location.propertyName,
    ) &&
    parent?.partType !== undefined
  ) {
    return {
      kind: "characterPart",
      ...base,
      partType: parent.partType as string | number,
    };
  }
  if (
    location.propertyName === "tag" ||
    location.propertyName === "propState" ||
    location.propertyName === "inspectDefinitionId"
  ) {
    const parentPropSlot = findParentPropSlot(location);
    if (!parentPropSlot) return undefined;
    return {
      kind: "propMetadata",
      ...base,
      parentPropSlot,
      metadataType:
        location.propertyName === "propState"
          ? "state"
          : location.propertyName === "inspectDefinitionId"
            ? "inspectDefinition"
            : "tag",
    };
  }

  const actor = lookup?.actorsById.get(sourceId);
  const prop = lookup?.propsById.get(sourceId);
  if (actorFieldNames.has(location.propertyName)) {
    // Bubble and Look At targets can be Props. Typed source lookup removes the
    // ambiguity; unknown-source payloads deliberately fall back to Actor and
    // are cleared either way.
    if (prop && ["npcId", "targetId"].includes(location.propertyName)) {
      return {
        kind: "prop",
        ...base,
        propId: prop.propId,
        prefab: prop.prefabName,
        name: prop.name,
        environmentMetadataId: prop.environmentMetadataId,
      };
    }
    return { kind: "actor", ...base, ...actor };
  }
  if (
    propFieldNames.has(location.propertyName) ||
    (location.propertyName === "source" &&
      location.ownerType === InteractionType.DynamicInteraction)
  ) {
    return {
      kind: "prop",
      ...base,
      propId: prop?.propId,
      prefab: prop?.prefabName,
      name: prop?.name,
      environmentMetadataId: prop?.environmentMetadataId,
    };
  }
  return undefined;
}

export function buildClipboardSourceContext(
  state: RootState,
  container?: ClipboardContainerLocator,
): ClipboardSourceContext | undefined {
  const trainingId = state.projects?.currentTrainingId ?? "";
  if (!trainingId) return undefined;
  return {
    trainingId,
    organizationId: state.user?.currentOrganization?.id ?? "",
    environmentId: state.environment?.currentEnvironmentId ?? "",
    ...(container && { container }),
  };
}

export function collectClipboardDependencies(
  value: unknown,
  state: RootState,
  inSequence = false,
): ClipboardDependency[] {
  const lookup = getSourceLookup(state);
  const dependencies: ClipboardDependency[] = [];
  const seen = new Set<string>();
  walkReferenceValues(
    value,
    (sourceId, location) => {
      if (sourceId === "player" && actorFieldNames.has(location.propertyName)) {
        const dependency = inferDependency(sourceId, location, lookup);
        if (dependency) dependencies.push(dependency);
        return;
      }
      const dependency = inferDependency(sourceId, location, lookup);
      if (!dependency) return;
      const key = `${dependency.interactionId}|${dependency.slot}|${dependency.kind}`;
      if (seen.has(key)) return;
      seen.add(key);
      dependencies.push(dependency);
    },
    inSequence,
  );
  return dependencies;
}

export function buildClipboardDependencyMetadata(
  value: unknown,
  state: RootState,
  container?: ClipboardContainerLocator,
): {
  sourceContext?: ClipboardSourceContext;
  dependencies?: ClipboardDependency[];
} {
  const sourceContext = buildClipboardSourceContext(state, container);
  if (!sourceContext) return {};
  const dependencies = collectClipboardDependencies(
    value,
    state,
    container?.kind === "sequence",
  );
  return {
    sourceContext,
    ...(dependencies.length > 0 && { dependencies }),
  };
}

interface DestinationContext {
  trainingId: string;
  organizationId: string;
  environmentId: string;
  takePropIds: Set<string>;
  props: Prop[];
  positions: Set<string>;
  legacyPositions: Set<string>;
  cameras: Set<string>;
  takeLocations: Set<string>;
  propMetadataByBackendId: Map<string, PropMetadata>;
  outfitNames: Set<string>;
  assetTypesByGuid: Map<string, ClipboardAssetDependency["expectedType"]>;
  token: string;
}

function assetTypeFromKey(key: AssetKey | undefined) {
  if (key === AssetKey.Image) return "image" as const;
  if (key === AssetKey.Video) return "video" as const;
  if (key === AssetKey.Audio) return "audio" as const;
  if (key === AssetKey.Pdf) return "pdf" as const;
  return "unknown" as const;
}

// Situation-backed positions differ per take, so validation passes the take
// that owns the interaction; paste resolves against the selected take.
function buildDestinationContext(
  state: RootState,
  take?: Partial<Take3D>,
): DestinationContext {
  const selectedTake =
    take ??
    (state.training?.takes3D ?? []).find(
      (candidate) => candidate.id === state.training?.selectedTake,
    );
  const positions = new Set<string>();
  const legacyPositions = new Set<string>();
  const cameras = new Set<string>();
  const takeLocations = new Set<string>();

  const addPositionReferences = (metadata: unknown, prefix = "") => {
    if (!isRecord(metadata)) return;
    const addPositions = (value: unknown) => {
      if (!Array.isArray(value)) return;
      value.filter(isRecord).forEach((position) => {
        if (typeof position.id === "string") {
          positions.add(`${prefix}${position.id}`);
          if (prefix) legacyPositions.add(position.id);
        }
      });
    };
    addPositions(metadata.actor_positions);
    addPositions(metadata.prop_positions);
    if (Array.isArray(metadata.camera_positions)) {
      metadata.camera_positions.filter(isRecord).forEach((camera) => {
        if (typeof camera.id === "string") {
          // Set Camera stores the raw camera ID together with takeLocationId.
          cameras.add(camera.id);
          // Look At stores a situation-qualified camera position ID.
          positions.add(`${prefix}${camera.id}`);
        }
      });
    }
  };

  (state.environment?.interactionPoints ?? []).forEach((takeLocation) => {
    takeLocations.add(takeLocation.id);
    const situationId = selectedTake?.situations?.[takeLocation.id];
    if (!situationId) {
      addPositionReferences(takeLocation);
      return;
    }
    // Set Camera identifies a situation-backed location by its instance ID.
    takeLocations.add(situationId);

    const situationPreset = (state.training?.props ?? []).find(
      (prop) => prop.id === situationId,
    );
    const situationMetadata = situationPreset
      ? state.propLibrary?.usedProps?.[situationPreset.propId]?.metadata
      : undefined;
    addPositionReferences(situationMetadata, `${situationId}/`);
  });

  const propMetadataByBackendId = new Map<string, PropMetadata>();
  Object.values(state.propLibrary?.usedProps ?? {}).forEach((usedProp) => {
    if (usedProp?.propContent?.id && usedProp.metadata) {
      propMetadataByBackendId.set(usedProp.propContent.id, usedProp.metadata);
    }
  });


  const assetTypesByGuid = new Map<
    string,
    ClipboardAssetDependency["expectedType"]
  >();
  const addAsset = (asset: unknown) => {
    if (!isRecord(asset)) return;
    const guid =
      typeof asset.assetId === "string" && asset.assetId
        ? asset.assetId
        : typeof asset.id === "string"
          ? asset.id
          : "";
    if (!guid) return;
    assetTypesByGuid.set(
      guid,
      assetTypeFromKey(asset.key as AssetKey | undefined),
    );
  };
  [
    ...(state.assets?.cachedAssets ?? []),
    ...(state.assets?.cachedAssets_small ?? []),
    ...(state.assets?.cachedAssets_medium ?? []),
    ...(state.assets?.assetsInCurrentFolder ?? []),
    ...(state.assets?.usedAssets ?? []),
    ...(state.assets?.legacyAssets ?? []),
    ...(state.assets?.folders ?? []).flatMap((folder) => folder.assets ?? []),
  ].forEach(addAsset);

  return {
    trainingId: state.projects?.currentTrainingId ?? "",
    organizationId: state.user?.currentOrganization?.id ?? "",
    environmentId: state.environment?.currentEnvironmentId ?? "",
    takePropIds: new Set(
      (selectedTake?.props ?? []).map((prop) => prop.instanceId),
    ),
    props: state.training?.props ?? [],
    positions,
    legacyPositions,
    cameras,
    takeLocations,
    propMetadataByBackendId,
    outfitNames: new Set(
      Object.keys(state.metadata?.knownOutfits ?? {}),
    ),
    assetTypesByGuid,
    token: state.authentication?.jwt ?? "",
  };
}

function isAvailableEnvironmentPosition(
  destination: DestinationContext,
  sourceId: string,
  propertyName: string,
): boolean {
  return (
    destination.positions.has(sourceId) ||
    (environmentPositionFieldNames.has(propertyName) &&
      destination.legacyPositions.has(sourceId))
  );
}

function dependencyKey(interactionId: string, slot: string): string {
  return `${interactionId}|${slot}`;
}

function incrementCleared(
  counts: Partial<Record<ClipboardDependencyKind, number>>,
  kind: ClipboardDependencyKind,
) {
  counts[kind] = (counts[kind] ?? 0) + 1;
}

function assetTypeMatches(
  expected: ClipboardAssetDependency["expectedType"],
  actual: ClipboardAssetDependency["expectedType"],
) {
  return (
    expected === undefined ||
    expected === "unknown" ||
    actual === "unknown" ||
    actual === expected
  );
}

// Play/Stop Sub Sequence store the target SubSequence's sequenceName in
// `nodeToPlay` (the compiler resolves it by name), not a node id.
function collectEmbeddedSubSequenceNames(value: unknown): Set<string> {
  const names = new Set<string>();
  const visited = new Set<object>();
  const visit = (current: unknown) => {
    if (current === null || typeof current !== "object") return;
    if (visited.has(current as object)) return;
    visited.add(current as object);
    if (
      isRecord(current) &&
      current.type === InteractionType.SubSequence &&
      typeof current.sequenceName === "string"
    ) {
      names.add(current.sequenceName);
    }
    (Array.isArray(current) ? current : Object.values(current)).forEach(visit);
  };
  visit(value);
  return names;
}

interface PendingAssetReference {
  dependency: ClipboardAssetDependency;
  location: WalkLocation;
}

export interface ClipboardDependencyResolution {
  value: unknown;
  crossTraining: boolean;
  cleared: Partial<Record<ClipboardDependencyKind, number>>;
  clearedCount: number;
  /** Pasted outfits that were not known yet but exist; add them to the known outfits. */
  verifiedOutfits?: Outfit[];
}

export async function resolveClipboardDependencies<T>(
  value: T,
  envelope: ClipboardEnvelope,
  state: RootState,
): Promise<ClipboardDependencyResolution & { value: T }> {
  const clone = cloneClipboardValue(value);
  const destination = buildDestinationContext(state);
  const sourceContext =
    envelope.version === 2 ? envelope.sourceContext : undefined;
  // Version 1/raw JSON retains its historical behavior. The conservative
  // unknown-source policy is the compatibility rule for v2 envelopes whose
  // optional source metadata is absent or malformed.
  const crossTraining =
    envelope.version === 2 &&
    (!sourceContext || sourceContext.trainingId !== destination.trainingId);
  const cleared: Partial<Record<ClipboardDependencyKind, number>> = {};
  const metadata =
    envelope.version === 2 &&
    Array.isArray(envelope.dependencies) &&
    envelope.dependencies.every(isClipboardDependency)
      ? envelope.dependencies
      : [];
  const metadataBySlot = new Map(
    metadata.map((dependency) => [
      dependencyKey(dependency.interactionId, dependency.slot),
      dependency,
    ]),
  );
  const resolvedProps = new Map<
    string,
    { prop: Prop; metadata?: PropMetadata }
  >();
  const pendingPropMetadata: Array<{
    dependency: ClipboardPropMetadataDependency;
    location: WalkLocation;
  }> = [];
  const pendingAssets: PendingAssetReference[] = [];
  // outfit names the destination has not seen yet; verified with the API below
  const pendingOutfits: Array<{ sourceId: string; location: WalkLocation }> = [];
  const embeddedSubSequenceNames = collectEmbeddedSubSequenceNames(clone);

  if (crossTraining && isRecord(clone)) {
    // Take-level placement maps refer to destination content that this feature
    // must never create. Interaction-level references are handled below.
    const cloneRecord = clone as RecordValue;
    if (isRecord(cloneRecord.attachments)) cloneRecord.attachments = {};
    if (isRecord(cloneRecord.situations)) cloneRecord.situations = {};
  }

  const clearLocation = (
    location: WalkLocation,
    kind: ClipboardDependencyKind,
  ) => {
    (location.parent as Record<string | number, unknown>)[location.key] = "";
    incrementCleared(cleared, kind);
  };

  walkReferenceValues(
    clone,
    (sourceId, location) => {
      const dependency =
        metadataBySlot.get(dependencyKey(location.ownerId, location.slot)) ??
        inferDependency(sourceId, location);
      if (!dependency) return;

      if (dependency.kind === "sequenceNode") {
        if (crossTraining && !embeddedSubSequenceNames.has(sourceId)) {
          clearLocation(location, dependency.kind);
        }
        return;
      }

      if (!crossTraining) return;

      switch (dependency.kind) {
        case "actor":
          if (sourceId !== "player") clearLocation(location, dependency.kind);
          return;
        case "prop": {
          const propDependency = dependency as ClipboardPropDependency;
          const canMapEnvironmentProp =
            Boolean(propDependency.environmentMetadataId) &&
            sourceContext?.environmentId === destination.environmentId;
          const candidates = canMapEnvironmentProp
            ? destination.props.filter(
                (prop) =>
                  prop.propType === PropType.Environment &&
                  prop.environmentMetadataId ===
                    propDependency.environmentMetadataId &&
                  destination.takePropIds.has(prop.id),
              )
            : [];
          if (candidates.length !== 1) {
            clearLocation(location, dependency.kind);
            return;
          }
          const mappedProp = candidates[0];
          (location.parent as Record<string | number, unknown>)[location.key] =
            mappedProp.id;
          resolvedProps.set(dependencyKey(location.ownerId, location.slot), {
            prop: mappedProp,
            metadata: destination.propMetadataByBackendId.get(
              mappedProp.propId,
            ),
          });
          return;
        }
        case "propMetadata":
          pendingPropMetadata.push({
            dependency: dependency as ClipboardPropMetadataDependency,
            location,
          });
          return;
        case "environmentPosition":
          if (
            sourceContext?.environmentId !== destination.environmentId ||
            !isAvailableEnvironmentPosition(
              destination,
              sourceId,
              location.propertyName,
            )
          ) {
            clearLocation(location, dependency.kind);
          }
          return;
        case "environmentCamera":
          if (
            sourceContext?.environmentId !== destination.environmentId ||
            !destination.cameras.has(sourceId)
          ) {
            clearLocation(location, dependency.kind);
          }
          return;
        case "takeLocation":
          if (
            sourceContext?.environmentId !== destination.environmentId ||
            !destination.takeLocations.has(sourceId)
          ) {
            clearLocation(location, dependency.kind);
          }
          return;
        case "asset":
          pendingAssets.push({
            dependency: dependency as ClipboardAssetDependency,
            location,
          });
          return;
        case "variable":
          if (!isBuiltInVariableId(sourceId)) {
            clearLocation(location, dependency.kind);
          }
          return;
        case "outfit":
          if (!destination.outfitNames.has(sourceId)) {
            pendingOutfits.push({ sourceId, location });
          }
          return;
        case "characterPart":
          // Character parts are a global catalog shared by every training.
          return;
      }
    },
    sourceContext?.container?.kind === "sequence",
  );

  pendingPropMetadata.forEach(({ dependency, location }) => {
    const parent = resolvedProps.get(
      dependencyKey(dependency.interactionId, dependency.parentPropSlot),
    );
    const metadata = parent?.metadata;
    const valid =
      dependency.metadataType === "tag"
        ? metadata?.clickableTags?.some(
            (tag) => tag.name === dependency.sourceId,
          ) ||
          metadata?.attachmentTags?.some(
            (tag) => tag.tag === dependency.sourceId,
          )
        : dependency.metadataType === "state"
          ? metadata?.prop_states?.some(
              (state) => state.name === dependency.sourceId,
            )
          : metadata?.inspectDefinitions?.some(
              (definition) => definition.id === dependency.sourceId,
            );
    if (!valid) clearLocation(location, dependency.kind);
  });

  const lookupPromises = new Map<
    string,
    Promise<{ found: boolean; type: ClipboardAssetDependency["expectedType"] }>
  >();
  const resolveAsset = (dependency: ClipboardAssetDependency) => {
    const cachedType = destination.assetTypesByGuid.get(dependency.sourceId);
    if (cachedType !== undefined) {
      return Promise.resolve({ found: true, type: cachedType });
    }
    if (!destination.organizationId || !destination.token) {
      return Promise.resolve({ found: false, type: "unknown" as const });
    }
    const cacheKey = `${destination.organizationId}|${dependency.sourceId}`;
    const existing = lookupPromises.get(cacheKey);
    if (existing) return existing;
    const request = AssetService.GetAssetByGuid(
      destination.organizationId,
      destination.token,
      dependency.sourceId,
      false,
      "small",
    )
      .then((asset) => ({
        found: Boolean(asset),
        type: assetTypeFromKey(asset?.key),
      }))
      .catch(() => ({ found: false, type: "unknown" as const }));
    lookupPromises.set(cacheKey, request);
    return request;
  };

  await Promise.all(
    pendingAssets.map(async ({ dependency, location }) => {
      const result = await resolveAsset(dependency);
      if (
        !result.found ||
        !assetTypeMatches(dependency.expectedType, result.type)
      ) {
        clearLocation(location, dependency.kind);
      }
    }),
  );

  // the pickers load the outfit catalog lazily, so an outfit missing from the
  // known names may still exist; only names the API does not know are cleared
  const verifiedOutfits =
    pendingOutfits.length > 0 && destination.organizationId
      ? await OutfitService.GetOutfitsByName(
          destination.organizationId,
          pendingOutfits.map(({ sourceId }) => sourceId),
        )
      : [];
  const verifiedOutfitNames = new Set(verifiedOutfits.map(({ name }) => name));
  pendingOutfits.forEach(({ sourceId, location }) => {
    if (!verifiedOutfitNames.has(sourceId)) clearLocation(location, "outfit");
  });

  const clearedCount = Object.values(cleared).reduce(
    (total, count) => total + (count ?? 0),
    0,
  );
  return { value: clone, crossTraining, cleared, clearedCount, verifiedOutfits };
}

const dependencyLabels: Record<ClipboardDependencyKind, string> = {
  actor: "Actor",
  prop: "Prop",
  propMetadata: "Prop metadata",
  environmentPosition: "position",
  environmentCamera: "camera",
  takeLocation: "Take location",
  asset: "asset",
  variable: "variable",
  sequenceNode: "Sequence target",
  outfit: "outfit",
  characterPart: "character part",
};

export function formatClipboardDependencyWarning(
  counts: Partial<Record<ClipboardDependencyKind, number>>,
): string {
  const summary = (Object.keys(dependencyLabels) as ClipboardDependencyKind[])
    .filter((kind) => (counts[kind] ?? 0) > 0)
    .map((kind) => {
      const count = counts[kind] ?? 0;
      return `${count} ${dependencyLabels[kind]}${count === 1 ? "" : "s"}`;
    })
    .join(", ");
  return `Pasted with unresolved references cleared (${summary}). Review validation errors.`;
}

/**
 * Validation uses the same adapter/inference contract as paste so cleared
 * required fields and stale non-empty references remain visible after the
 * one-shot paste warning disappears.
 */
export function getClipboardReferenceValidationErrors(
  interaction: Interaction,
  state: RootState | undefined,
  take: unknown,
): string[] {
  if (!state) return [];
  const adapter = clipboardReferenceAdapters[interaction.type];
  if (adapter.portability === "portable") return [];

  const takeRecord = isRecord(take) ? take : {};
  const destination = buildDestinationContext(
    state,
    takeRecord as Partial<Take3D>,
  );
  const lookup = getSourceLookup(state);
  const takeActorIds = new Set(
    Array.isArray(takeRecord.actors)
      ? takeRecord.actors
          .filter(isRecord)
          .map((actor) => actor.actorId)
          .filter((id): id is string => typeof id === "string")
      : [],
  );
  const localSubSequenceNames = collectEmbeddedSubSequenceNames(interaction);
  const errors = new Set<string>();

  walkReferenceValues(interaction, (sourceId, location) => {
    const dependency = inferDependency(sourceId, location, lookup);
    if (!dependency) return;
    let valid = true;
    switch (dependency.kind) {
      case "actor":
        valid =
          sourceId === "player" ||
          (lookup.actorsById.has(sourceId) && takeActorIds.has(sourceId));
        break;
      case "prop":
        valid = lookup.propsById.has(sourceId);
        break;
      case "environmentPosition":
        valid = isAvailableEnvironmentPosition(
          destination,
          sourceId,
          location.propertyName,
        );
        break;
      case "environmentCamera":
        valid = destination.cameras.has(sourceId);
        break;
      case "takeLocation":
        valid = destination.takeLocations.has(sourceId);
        break;
      case "variable":
        // Actor-name variables (GV_ActorName_<id>) are derived from the
        // training's actors and never stored in sequenceVariables.
        valid =
          isReservedSequenceVariableId(sourceId, state.training?.actors) ||
          lookup.variablesById.has(sourceId);
        break;
      case "sequenceNode":
        valid = localSubSequenceNames.has(sourceId);
        break;
      case "outfit":
        valid = destination.outfitNames.has(sourceId);
        break;
      case "asset":
      case "propMetadata":
        // Asset accessibility is asynchronous and Prop metadata validity is
        // already parent-dependent; paste clears both conservatively.
        return;
      case "characterPart":
        // The character part catalog is global and only loaded page by page
        // on demand, so an unloaded part is not a missing one.
        return;
    }
    if (!valid) {
      errors.add(
        `${dependencyLabels[dependency.kind]} reference is unavailable (${location.slot})`,
      );
    }
  });

  const visited = new Set<object>();
  const visitRequired = (value: unknown, path = "") => {
    if (value === null || typeof value !== "object") return;
    if (visited.has(value as object)) return;
    visited.add(value as object);
    const entries = Array.isArray(value)
      ? value.map((child, index) => [index, child] as const)
      : Object.entries(value);
    entries.forEach(([key, child]) => {
      const propertyName = typeof key === "number" ? "" : String(key);
      const slot = appendSlot(path, key);
      const parent = Array.isArray(value) ? undefined : (value as RecordValue);
      const isGenericActorValue =
        propertyName === "value" &&
        (parent?.type === InteractionPropertyType.actor ||
          parent?.type === DynamicInteractionPropertyType.actorDropdown);
      if (
        typeof child === "string" &&
        child.trim() === "" &&
        (requiredFieldNames.has(propertyName) || isGenericActorValue)
      ) {
        errors.add(`Required reference is missing (${slot})`);
      } else if (child !== null && typeof child === "object") {
        visitRequired(child, slot);
      }
    });
  };
  visitRequired(interaction);

  return [...errors];
}
